/**
 * ChecklistDetailSheetHeader — Redesigned header for the checklist detail sheet
 *
 * Information-dense header with:
 *   - Nav row (prev/next counter + filter + overflow menu + close)
 *   - Item name + status badge + complete/uncomplete button
 *   - Completion readiness progress bar
 *   - Quick stats strip (questions, docs, findings, due date)
 */

import * as Dialog from "@radix-ui/react-dialog";
import {
  ChevronLeft,
  ChevronRight,
  X,
  CheckCircle2,
  RotateCcw,
  ListFilter,
  MessageSquare,
  FileText,
  AlertTriangle,
  Calendar,
  MoreVertical,
  Pencil,
  Trash2,
} from "lucide-react";
import { motion } from "framer-motion";
import { Button, Dropdown, SideSheet } from "@/components/ui";
import { useChecklistStatusConfig } from "./ChecklistRow";
import type { UIChecklistItem } from "@/packages/types";
import { useTranslation } from "@/packages/utils";

export type NavFilter = "all" | "open" | "completed";

// ─── Progress Calculation ────────────────────────────────────────────────────

function calculateProgress(item: UIChecklistItem): number {
  const questions = item.questions ?? [];
  const answers = item.answers ?? [];
  const answeredOrders = new Set(answers.map((a) => a.question.order));

  const questionScore =
    questions.length > 0 ? (answeredOrders.size / questions.length) * 40 : 40; // full weight if no questions
  const evidenceScore = (item.linkedDocumentIds?.length ?? 0) > 0 ? 30 : 0;
  const aiScore = item.summaryReport ? 20 : 0;
  const statusScore = item.status === "APPROVED" ? 10 : 0;
  return Math.round(questionScore + evidenceScore + aiScore + statusScore);
}

// ─── Quick Stats Strip ───────────────────────────────────────────────────────

function QuickStats({
  item,
  findingsCount,
  onJumpToTab,
}: {
  item: UIChecklistItem;
  findingsCount: number;
  onJumpToTab: (tab: string) => void;
}) {
  const questions = item.questions ?? [];
  const answers = item.answers ?? [];
  const answeredOrders = new Set(answers.map((a) => a.question.order));
  const docCount = item.linkedDocumentIds?.length ?? 0;

  const dueDate = item.dueDate
    ? new Date(item.dueDate).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      })
    : null;

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {questions.length > 0 && (
        <button
          type="button"
          onClick={() => onJumpToTab("questions")}
          className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-page hover:bg-border/80 transition-colors text-[11px] text-secondary cursor-pointer"
        >
          <MessageSquare size={11} className="text-muted" />
          <span>
            {answeredOrders.size}/{questions.length} Questions
          </span>
        </button>
      )}
      <button
        type="button"
        onClick={() => onJumpToTab("evidence")}
        className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-page hover:bg-border/80 transition-colors text-[11px] text-secondary cursor-pointer"
      >
        <FileText size={11} className="text-muted" />
        <span>
          {docCount} {docCount === 1 ? "Doc" : "Docs"}
        </span>
      </button>
      {findingsCount > 0 && (
        <button
          type="button"
          onClick={() => onJumpToTab("questions")}
          className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-500/10 hover:bg-red-500/15 transition-colors text-[11px] text-red-600 cursor-pointer"
        >
          <AlertTriangle size={11} />
          <span>
            {findingsCount} {findingsCount === 1 ? "Finding" : "Findings"}
          </span>
        </button>
      )}
      {dueDate && (
        <span className="flex items-center gap-1.5 px-2 py-1 text-[11px] text-muted">
          <Calendar size={11} />
          <span>{dueDate}</span>
        </span>
      )}
    </div>
  );
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface SheetHeaderProps {
  item: UIChecklistItem;
  currentIndex: number;
  totalCount: number;
  onPrev: () => void;
  onNext: () => void;
  onEdit?: () => void;
  navFilter: NavFilter;
  onNavFilterChange: (filter: NavFilter) => void;
  onComplete: () => void;
  onUncomplete: () => void;
  onRemoveFromAudit: () => void;
  findingsCount: number;
  onJumpToTab: (tab: string) => void;
}

const NAV_FILTER_KEYS: Record<NavFilter, string> = {
  all: "checklist.nav.all",
  open: "checklist.nav.open",
  completed: "checklist.nav.completed",
};

// ─── Main Export ─────────────────────────────────────────────────────────────

export function SheetHeader({
  item,
  currentIndex,
  totalCount,
  onPrev,
  onNext,
  onEdit,
  navFilter,
  onNavFilterChange,
  onComplete,
  onUncomplete,
  onRemoveFromAudit,
  findingsCount,
  onJumpToTab,
}: SheetHeaderProps) {
  const { t } = useTranslation("audits");
  const statusConfigs = useChecklistStatusConfig();
  const statusConfig = statusConfigs[item.status];

  const progress = calculateProgress(item);

  const progressColor =
    progress >= 80 ? "#22c55e" : progress >= 50 ? "#E8734A" : "#94a3b8";

  return (
    <SideSheet.Header>
      {/* Row 1: Navigation + overflow + close */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={onPrev}
            title="Previous (k)"
            className="h-7 w-7 rounded-md flex items-center justify-center text-muted hover:text-primary hover:bg-card-hover transition-colors cursor-pointer"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="text-[11px] text-muted font-medium tabular-nums px-1 select-none">
            {currentIndex + 1} / {totalCount}
          </span>
          <button
            type="button"
            onClick={onNext}
            title="Next (j)"
            className="h-7 w-7 rounded-md flex items-center justify-center text-muted hover:text-primary hover:bg-card-hover transition-colors cursor-pointer"
          >
            <ChevronRight size={14} />
          </button>

          <Dropdown>
            <Dropdown.Trigger>
              <button
                type="button"
                className="ml-0.5 h-6 px-2 rounded-md flex items-center gap-1 text-[11px] text-muted hover:text-secondary hover:bg-card-hover transition-colors cursor-pointer"
              >
                <ListFilter size={10} />
                <span>
                  {
                    t(
                      NAV_FILTER_KEYS[navFilter] as "checklist.nav.all",
                    ) as string
                  }
                </span>
              </button>
            </Dropdown.Trigger>
            <Dropdown.Content side="bottom" align="start">
              <Dropdown.Item onSelect={() => onNavFilterChange("all")}>
                {t("checklist.nav.all")}
              </Dropdown.Item>
              <Dropdown.Item onSelect={() => onNavFilterChange("open")}>
                {t("checklist.nav.open")}
              </Dropdown.Item>
              <Dropdown.Item onSelect={() => onNavFilterChange("completed")}>
                {t("checklist.nav.completed")}
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown>
        </div>

        <div className="flex items-center gap-0.5">
          <Dropdown>
            <Dropdown.Trigger>
              <button
                type="button"
                title="More actions"
                className="h-7 w-7 rounded-md flex items-center justify-center text-muted hover:text-primary hover:bg-card-hover transition-colors cursor-pointer"
              >
                <MoreVertical size={14} />
              </button>
            </Dropdown.Trigger>
            <Dropdown.Content align="end">
              {onEdit && (
                <Dropdown.Item icon={<Pencil size={13} />} onSelect={onEdit}>
                  {t("checklist.detail.editRequirement")}
                </Dropdown.Item>
              )}
              <Dropdown.Item
                destructive
                icon={<Trash2 size={13} />}
                onSelect={onRemoveFromAudit}
              >
                {t("checklist.footer.removeFromAudit")}
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown>
          <Dialog.Close
            className="h-7 w-7 rounded-md flex items-center justify-center text-muted hover:text-primary hover:bg-card-hover transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X size={14} />
          </Dialog.Close>
        </div>
      </div>

      {/* Row 2: Item name + status badge + complete button */}
      <div className="mt-3 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Dialog.Title className="font-['Crimson_Pro'] text-[21px] font-semibold text-primary leading-snug tracking-tight">
            {item.name}
          </Dialog.Title>
          <Dialog.Description className="mt-1 flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase"
              style={{
                backgroundColor: statusConfig.badgeBg,
                color: statusConfig.badgeText,
                borderColor: `${statusConfig.badgeText}30`,
              }}
            >
              <span
                className="size-1.5 rounded-full shrink-0"
                style={{ backgroundColor: statusConfig.dotColor }}
              />
              {statusConfig.label}
            </span>
            {item.required && (
              <span className="text-[10px] font-semibold text-red-500/80 uppercase tracking-wide">
                Required
              </span>
            )}
            {item.completedBy && item.completedAt && (
              <span className="text-[11px] text-muted">
                · completed by {item.completedBy}
              </span>
            )}
          </Dialog.Description>
        </div>

        <div className="shrink-0 pt-0.5">
          {item.status === "APPROVED" ? (
            <Button
              variant="secondary"
              size="sm"
              icon={RotateCcw}
              onClick={onUncomplete}
              className="h-8 text-[12px]"
            >
              {t("checklist.footer.markIncomplete")}
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              icon={CheckCircle2}
              onClick={onComplete}
              className={`h-8 text-[12px] transition-all ${
                progress >= 70 ? "shadow-[0_0_10px_rgba(90,138,114,0.35)]" : ""
              }`}
            >
              {t("checklist.footer.markApproved")}
            </Button>
          )}
        </div>
      </div>

      {/* Row 3: Completion readiness progress bar */}
      <div className="mt-3 space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-medium text-muted uppercase tracking-wider">
            Completion Readiness
          </span>
          <span
            className="text-[11px] font-semibold tabular-nums"
            style={{ color: progressColor }}
          >
            {progress}%
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-border/60 overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: progressColor }}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
      </div>

      {/* Row 4: Quick stats chips */}
      <div className="mt-3 pt-3 border-t border-border/40">
        <QuickStats
          item={item}
          findingsCount={findingsCount}
          onJumpToTab={onJumpToTab}
        />
      </div>
    </SideSheet.Header>
  );
}
